/**
 * Shared CSS rules used by all widget templates.
 *
 * Templates call `baseStyles(accent)` then append their own trigger, panel,
 * and any override CSS. Because template-specific rules appear later in the
 * concatenated string they win via normal CSS cascade — no !important needed.
 */
export function baseStyles(accent: string): string {
  return `
    * { margin: 0; padding: 0; box-sizing: border-box; }

    .yd-header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      background: ${accent};
      color: white;
      min-height: 56px;
    }
    .yd-header-logo {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
    }
    .yd-header-name {
      flex: 1;
      font-size: 15px;
      font-weight: 600;
    }
    .yd-header-btn {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 4px;
      opacity: 0.8;
      transition: opacity 0.15s;
      display: flex;
      align-items: center;
    }
    .yd-header-btn:hover { opacity: 1; }

    .yd-messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .yd-suggestions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      justify-content: center;
      padding: 0 16px 12px;
    }
    .yd-suggestion {
      background: #f3f4f6;
      border: 1px solid #e5e7eb;
      border-radius: 20px;
      padding: 6px 14px;
      font-size: 13px;
      color: #374151;
      cursor: pointer;
      transition: background 0.15s;
    }
    .yd-suggestion:hover {
      background: #e5e7eb;
    }

    .yd-msg {
      max-width: 85%;
      padding: 10px 14px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
    }
    .yd-msg-user {
      width: fit-content;
      margin-left: auto;
      background: ${accent};
      color: white;
      border-bottom-right-radius: 4px;
      white-space: pre-wrap;
    }
    .yd-msg-assistant {
      align-self: flex-start;
      background: #f3f4f6;
      color: #1f2937;
      border-bottom-left-radius: 4px;
    }

    .yd-msg-assistant p { margin: 0 0 8px 0; }
    .yd-msg-assistant p:last-child { margin-bottom: 0; }

    .yd-msg-assistant h1,
    .yd-msg-assistant h2,
    .yd-msg-assistant h3,
    .yd-msg-assistant h4,
    .yd-msg-assistant h5,
    .yd-msg-assistant h6 {
      font-weight: 700;
      margin: 8px 0 4px 0;
    }
    .yd-msg-assistant h1:first-child,
    .yd-msg-assistant h2:first-child,
    .yd-msg-assistant h3:first-child,
    .yd-msg-assistant h4:first-child,
    .yd-msg-assistant h5:first-child,
    .yd-msg-assistant h6:first-child { margin-top: 0; }
    .yd-msg-assistant h1 { font-size: 1.1em; }
    .yd-msg-assistant h2 { font-size: 1.05em; }
    .yd-msg-assistant h3,
    .yd-msg-assistant h4,
    .yd-msg-assistant h5,
    .yd-msg-assistant h6 { font-size: 1em; }

    .yd-msg-assistant a {
      color: ${accent};
      text-decoration: underline;
      word-break: break-all;
    }

    .yd-msg-assistant ul,
    .yd-msg-assistant ol {
      margin: 4px 0 8px 0;
      padding-left: 20px;
    }
    .yd-msg-assistant ul:last-child,
    .yd-msg-assistant ol:last-child { margin-bottom: 0; }
    .yd-msg-assistant li { margin: 2px 0; }

    .yd-msg-assistant code {
      background: rgba(0,0,0,0.06);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 0.9em;
      font-family: monospace;
    }
    .yd-msg-assistant pre {
      background: #1f2937;
      color: #f9fafb;
      padding: 10px 12px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 8px 0;
      font-size: 0.85em;
    }
    .yd-msg-assistant pre code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: inherit;
    }

    .yd-msg-assistant blockquote {
      border-left: 3px solid #d1d5db;
      padding-left: 10px;
      margin: 4px 0;
      color: #6b7280;
      font-style: italic;
    }

    .yd-msg-assistant hr {
      border: none;
      border-top: 1px solid #e5e7eb;
      margin: 8px 0;
    }

    .yd-msg-row {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      align-self: flex-start;
      max-width: 90%;
    }
    .yd-msg-row .yd-msg {
      align-self: auto;
    }
    .yd-msg-avatar {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .yd-typing {
      align-self: flex-start;
      padding: 10px 14px;
      background: #f3f4f6;
      border-radius: 16px;
      border-bottom-left-radius: 4px;
      font-size: 14px;
      color: #1f2937;
      max-width: 85%;
    }

    .yd-dots {
      display: inline-flex;
      gap: 4px;
    }
    .yd-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #9ca3af;
      animation: yd-bounce 1.4s infinite ease-in-out;
    }
    .yd-dot:nth-child(1) { animation-delay: 0s; }
    .yd-dot:nth-child(2) { animation-delay: 0.2s; }
    .yd-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes yd-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-6px); }
    }

    .yd-input-area {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      border-top: 1px solid #e5e7eb;
    }
    .yd-input {
      flex: 1;
      border: 1px solid #e5e7eb;
      border-radius: 24px;
      padding: 8px 16px;
      font-size: 14px;
      outline: none;
      font-family: inherit;
      transition: border-color 0.15s;
    }
    .yd-input:focus {
      border-color: ${accent};
    }
    .yd-send-btn {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: ${accent};
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.15s;
      flex-shrink: 0;
    }
    .yd-send-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .yd-error {
      text-align: center;
      padding: 8px 16px;
      font-size: 13px;
      color: #ef4444;
    }
    .yd-retry-btn {
      background: none;
      border: 1px solid #ef4444;
      color: #ef4444;
      border-radius: 16px;
      padding: 4px 12px;
      font-size: 12px;
      cursor: pointer;
      margin-top: 4px;
    }

    /* ── Lead capture form ───────────────────────────────── */

    .yd-lead-form-container {
      display: flex;
      flex-direction: column;
      padding: 16px;
      flex: 1;
      overflow-y: auto;
    }

    .yd-lead-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 16px 0;
      line-height: 1.4;
    }

    .yd-lead-form {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .yd-lead-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .yd-lead-label {
      font-size: 13px;
      font-weight: 500;
      color: #1f2937;
    }

    .yd-lead-input,
    .yd-lead-select {
      padding: 8px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      font-family: inherit;
      background: #fff;
      color: #1f2937;
      outline: none;
      transition: border-color 0.15s;
    }

    .yd-lead-input:focus,
    .yd-lead-select:focus {
      border-color: ${accent};
    }

    .yd-lead-field-error .yd-lead-input,
    .yd-lead-field-error .yd-lead-select {
      border-color: #ef4444;
    }

    .yd-lead-error-text {
      font-size: 12px;
      color: #ef4444;
    }

    .yd-lead-checkbox {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      font-size: 13px;
      color: #1f2937;
      cursor: pointer;
    }

    .yd-lead-checkbox input[type="checkbox"] {
      margin-top: 2px;
      accent-color: ${accent};
    }

    .yd-lead-submit {
      padding: 10px 16px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #fff;
      cursor: pointer;
      transition: opacity 0.15s;
      margin-top: 4px;
    }

    .yd-lead-submit:hover {
      opacity: 0.9;
    }

    .yd-lead-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .yd-lead-submit-error {
      font-size: 13px;
      color: #ef4444;
      padding: 4px 0;
    }

    .yd-lead-privacy {
      font-size: 11px;
      color: #9ca3af;
      margin: 4px 0 0 0;
      line-height: 1.4;
    }
  `;
}

/**
 * Default `.yd-panel` positioning shared by templates that use the standard
 * 380x520 floating panel (classic-bubble, greeting-card).
 */
export function defaultPanelStyles(position: string): string {
  const side = position === 'bottom-right' ? 'right: 20px;' : 'left: 20px;';
  return `
    .yd-panel {
      position: fixed;
      bottom: 84px;
      ${side}
      width: 380px;
      height: 520px;
      max-height: calc(100vh - 104px);
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.12);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      z-index: 1;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      pointer-events: auto;
    }

    @media (max-width: 480px) {
      .yd-panel {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        border-radius: 0;
      }
    }
  `;
}
